import { useState } from 'react'
import PropTypes from 'prop-types'
import { Bar } from 'react-chartjs-2'
import { Chart as ChartJS, registerables } from 'chart.js'
import { Typography } from '@mui/material'
import { Modal } from 'frame/components'
import moment from 'moment'
import { themeV5 } from 'theme/v5'

ChartJS.register(...registerables)

const StackedBarChart = ({ data, onClick, noDataMessage, showHealthAndSafetyDisclaimer }) => {
  const [openDisclaimerMessage, setOpenDisclaimerMessage] = useState(false)
  const defaultTickColor = themeV5.palette.graph.secondary
  const overdueTickColor = themeV5.palette.brand.red

  const isOverdueLabel = (label) => {
    const labels = Array.isArray(label) ? label : [label]
    return labels.some((text) => {
      if (typeof text !== 'string') {
        return false
      }
      const lower = text.trim().toLowerCase()
      return lower === 'expired' || lower === 'overdue' || lower === 'matured'
    })
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        stacked: true,
        border: { display: false },
        grid: {
          display: false,
        },
        ticks: {
          padding: 20,
          color: (context) => isOverdueLabel(context.tick?.label) ? overdueTickColor : defaultTickColor,
        }
      },
      y: {
        stacked: true,
        border: { display: false },
        grid: {
          borderDash: [2],
          color: themeV5.palette.divider,
        },
        min: 0,
        ticks: {
          padding: 20,
          color: defaultTickColor,
          maxTicksLimit: 5,
          precision: 0,
        }
      }
    },
    plugins: {
      legend: {
        display: false,
      },
    },
    layout: {
      padding: 0
    },
    onClick: (event, elements, chart) => {
      try {
        if (!elements.length) {
          return
        }
        const element = elements[0]
        const dataset = chart.data.datasets[element.datasetIndex]
        const xLabel = chart.data.labels[element.index]
        const value = dataset.data[element.index]

        const dateRange = data.ranges[element.index]
        const parseDateRange = dateRange.utc().date(1)
          .format('MM-DD-YYYY')
        const formattedDateRange = moment(parseDateRange, 'MM-DD-YYYY').format('MMMM YYYY')

        console.log(dataset.label + ' at ' + xLabel + ': ' + value)
        console.log({
          elements,
          element,
          dataset,
          data,
          dateRange,
          formattedDateRange,
        })

        if (element.index === 0) {
          onClick(null, null, dataset.label.includes('booked'))
        } else {
          onClick('range', formattedDateRange, dataset.label.includes('booked'))
        }
      }
      catch (err) {
        console.log('Error parsing stacked bar graph data: ', err.message) // eslint-disable-line
      }
    }
  }

  const dataAvailable = data.values.length > 1

  return (
    <>
      {dataAvailable ? (
        <Bar
          height={130}
          data={{
            labels: data.labels,
            responsive: true,
            offset: true,
            datasets: [
              {
                label: 'without booking',
                backgroundColor: data.colors,
                data: data.values,
                borderRadius: 0,
                barThickness: 32,
                maxBarThickness: 38,
                barPercentage: 0.5,
                categoryPercentage: 0.5,
              },
              {
                label: 'booked',
                backgroundColor: data.colors.map((_, i) =>
                  i === 0 ? themeV5.palette.accent.blue : themeV5.palette.accent.sky
                ),
                data: data.valuesStacked ? data.valuesStacked : new Array(data.values.length).fill(0),
                borderRadius: 0,
                barThickness: 32,
                maxBarThickness: 38,
                barPercentage: 0.5,
                categoryPercentage: 0.5,
              }
            ]
          }}
          options={options}/>
      ) : (
        <Typography variant="body1">
          {noDataMessage ? noDataMessage : 'No data available'}

          {showHealthAndSafetyDisclaimer && (
            <>
              <br />
              <span
                style={{
                  color: themeV5.palette.text.link,
                  textDecoration: 'none',
                  cursor: 'pointer',
                }}
                onClick={() => setOpenDisclaimerMessage(true)}>
                View Health and Safety Disclaimer
              </span>{'.'}
            </>
          )}
        </Typography>
      )}

      {openDisclaimerMessage && (
        <Modal
          open={openDisclaimerMessage}
          title="Health and Safety Disclaimer"
          onClose={() => setOpenDisclaimerMessage(false)}>
          Please note there are possible implications to your obligations both under your finance contract/lease and the Health
          and Safety at Work Act 2015 if the servicing requirements are not completed when due.
        </Modal>
      )}
    </>
  )
}

StackedBarChart.propTypes = {
  data: PropTypes.object.isRequired,
  onClick: PropTypes.func.isRequired,
  noDataMessage: PropTypes.string,
  showHealthAndSafetyDisclaimer: PropTypes.bool,
}

export default StackedBarChart
